// /settings/privacy — согласия и использование данных.
// Реальные данные:
//  • consents — consents.history (как в /settings/account)
//  • AI training — статический disclosure (мы НЕ обучаем на user-контенте,
//    compliance.md §13.2) → read-only, не фейк-toggle
//  • передача данных AI-сервисам — НАСТОЯЩИЙ тумблер (users.ai_data_sharing,
//    App Store 5.1.1(i)): «выкл» останавливает вызовы на сервере, в швах портов
//  • маркетинговые письма — это КАНАЛ, а не режим приватности: тумблер живёт
//    в /settings/notifications (notification_preferences.marketingEnabled →
//    зеркалится в users.marketing_opt_in, GDPR-гейт notify-throttled).
//    Здесь — только ссылка туда.
//  • аналитика и реклама — единое согласие экосистемы IEC (OXEM-604, гайд IEC
//    ID §3.6). Своего тумблера нет: строка показывает решение и открывает
//    выбор — в вебе баннер `consent.js` (`IecConsent.open()`), в приложении
//    нативный экран (`components/consent/native-consent-gate.tsx`).
// Блоки «Дисклеймеры» убраны (подсказки сбрасывались отдельной фичей,
// не нужной в MVP).
// См. docs/ui-spec/settings.privacy.{desktop,mobile}.md.

import { Button } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import {
	IconChartBar,
	IconChevronRight,
	IconCircleCheck,
	IconDatabase,
	IconDownload,
	IconExternalLink,
	IconFileExport,
	IconLock,
	IconShieldCheck,
	IconSparkles,
	IconSpeakerphone,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { AiDataSharingRow } from "@/components/settings/ai-data-sharing-row";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import { SettingsSection } from "@/components/settings/settings-shell";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	type ConsentState,
	getConsentState,
	iecConsentApi,
	onConsentChange,
} from "@/lib/ecosystem-analytics";
import { openIecIdSection } from "@/lib/iec-id-portal";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { api } from "@/lib/trpc-client";
import { useConsentScreenStore } from "@/stores/consent-screen";

export const Route = createFileRoute("/settings/privacy")({
	component: PrivacyRoute,
	pendingComponent: PagePendingFallback,
});

function PrivacyRoute() {
	const { t, i18n } = useTranslation("settings");

	const consentsQuery = api.consents.history.useQuery(undefined, {
		staleTime: 60_000,
	});
	// GDPR Art.20 / 152-ФЗ ст.20 — реальный экспорт данных (Inngest workflow).
	const dataExportMutation = api.users.requestDataExport.useMutation({
		onSuccess: () => {
			toast.success(t("privacy.dataExportSuccess"));
		},
		onError: (err) => {
			toast.error(
				err.message === "DATA_EXPORT_RATE_LIMITED"
					? t("privacy.dataExportRateLimited")
					: t("privacy.dataExportError"),
			);
		},
	});

	const dateFmt = useMemo(
		() => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }),
		[i18n.language],
	);
	const consents = useMemo(() => {
		const rows = consentsQuery.data ?? [];
		return rows.map((c) => ({
			id: c.id,
			label: `${c.documentType} v${c.documentVersion}`,
			date: dateFmt.format(new Date(c.acceptedAt)),
		}));
	}, [consentsQuery.data, dateFmt]);

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("privacy.title")}
				description={t("privacy.subtitle")}
				icon={<IconLock stroke={1.75} />}
			/>

			<SettingsSection
				title={t("privacy.consentsAccepted")}
				icon={<IconCircleCheck stroke={1.75} />}
			>
				{consentsQuery.isLoading ? (
					Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="px-4 py-3.5">
							<div className="h-4 w-48 animate-pulse rounded bg-muted" />
						</div>
					))
				) : consentsQuery.isError ? (
					<div className="px-4 py-3.5 text-sm text-destructive">
						{t("privacy.consentsLoadError")}
					</div>
				) : consents.length === 0 ? (
					<div className="px-4 py-3.5 text-sm text-muted-foreground">
						{t("privacy.consentsEmpty")}
					</div>
				) : (
					consents.map((c) => (
						<div
							key={c.id}
							className="flex items-center gap-2.5 px-4 py-3 text-sm transition-colors duration-fast hover:bg-[var(--surface-2)]"
						>
							<IconCircleCheck size={16} stroke={1.75} className="shrink-0 text-success" />
							<span className="flex-1 text-foreground">{c.label}</span>
							<span className="text-xs tabular-nums text-muted-foreground">{c.date}</span>
						</div>
					))
				)}
			</SettingsSection>

			<SettingsSection title={t("privacy.dataUsage")} icon={<IconDatabase stroke={1.75} />}>
				<div className="flex items-start gap-3 px-4 py-3.5">
					<IconSparkles size={18} stroke={1.75} className="mt-0.5 shrink-0 text-muted-foreground" />
					<div className="min-w-0 flex-1">
						<div className="text-sm font-medium text-foreground">
							{t("privacy.aiTrainingNoticeTitle")}
						</div>
						<div className="mt-0.5 text-xs text-muted-foreground">
							{t("privacy.aiTrainingNotice")}
						</div>
					</div>
					<span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-success/20 bg-success/15 px-2 py-0.5 text-2xs font-medium text-success">
						<IconShieldCheck size={11} stroke={1.75} />
						{t("privacy.aiTrainingDisabled")}
					</span>
				</div>
				{/* Разрешение на передачу данных AI (App Store 5.1.1(i)) — первым:
				    это главное решение о данных в продукте, построенном на AI. */}
				<AiDataSharingRow />
				<CookieChoicesRow />

				{/* Маркетинговые письма — это канал доставки, а не режим приватности:
				    тумблер живёт в «Уведомлениях» (там он читает реальное состояние
				    prefs; здешняя копия всегда стартовала с false и вводила в
				    заблуждение). Оставляем только указатель, где его искать. */}
				<Link
					to="/settings/notifications"
					className="flex items-start gap-3 px-4 py-3.5 transition-colors duration-fast hover:bg-[var(--surface-2)]"
				>
					<IconSpeakerphone
						size={18}
						stroke={1.75}
						className="mt-0.5 shrink-0 text-muted-foreground"
					/>
					<div className="min-w-0 flex-1">
						<div className="text-sm font-medium text-foreground">{t("privacy.marketing")}</div>
						<div className="mt-0.5 text-xs text-muted-foreground">
							{t("privacy.marketingMoved")}
						</div>
					</div>
					<IconChevronRight
						size={16}
						stroke={1.75}
						className="mt-0.5 shrink-0 text-muted-foreground"
					/>
				</Link>
			</SettingsSection>

			<SettingsSection title={t("privacy.dataExport")} icon={<IconFileExport stroke={1.75} />}>
				<div className="space-y-4 px-4 py-4">
					<div className="flex items-start gap-3">
						<IconDownload
							size={18}
							stroke={1.75}
							className="mt-0.5 shrink-0 text-muted-foreground"
						/>
						<div className="min-w-0 flex-1 space-y-3">
							<div>
								<div className="text-sm font-medium text-foreground">
									{t("privacy.dataExportHeading")}
								</div>
								<p className="mt-0.5 text-xs text-muted-foreground">
									{t("privacy.dataExportIntro")}
								</p>
							</div>

							<div className="space-y-1.5 text-xs">
								<p className="text-foreground">
									<span className="font-medium">{t("privacy.dataExportIncludesTitle")}</span>{" "}
									<span className="text-muted-foreground">{t("privacy.dataExportIncludes")}</span>
								</p>
								<p className="text-foreground">
									<span className="font-medium">{t("privacy.dataExportExcludesTitle")}</span>{" "}
									<span className="text-muted-foreground">{t("privacy.dataExportExcludes")}</span>
								</p>
							</div>

							<Button
								onClick={() => dataExportMutation.mutate({})}
								disabled={dataExportMutation.isPending}
								className="w-full pointer-coarse:h-11 sm:w-auto"
							>
								<IconFileExport size={16} stroke={1.75} className="mr-1.5" />
								{dataExportMutation.isPending
									? t("privacy.dataExportPending")
									: t("privacy.dataExportCta")}
							</Button>
						</div>
					</div>
				</div>
			</SettingsSection>
		</SettingsPageContainer>
	);
}

/**
 * Единое согласие экосистемы на аналитику и рекламу (OXEM-604). Своего
 * тумблера у сервиса нет и быть не должно (гайд IEC ID §3.5): строка лишь
 * показывает действующее решение и открывает ОБЩИЙ выбор — баннер
 * `consent.js` в вебе, нативный экран в приложении. Решение действует во
 * всех сервисах IEC сразу.
 */
function CookieChoicesRow(): React.ReactElement {
	const { t } = useTranslation(["settings", "consent"]);
	const state = useSyncExternalStore<ConsentState>(
		onConsentChange,
		getConsentState,
		getConsentState,
	);
	const openNativeScreen = useConsentScreenStore((s) => s.open);

	function onChange(): void {
		if (IS_CAPACITOR) {
			openNativeScreen();
			return;
		}
		const banner = iecConsentApi();
		// Баннера нет (не боевой хост, скрипт ещё грузится или заблокирован) —
		// тот же выбор живёт в Legal Center IEC ID.
		if (banner) banner.open();
		else openIecIdSection("legal");
	}

	const valueLabel = (value: ConsentState["analytics"]) =>
		value === "granted"
			? t("privacy.cookieChoiceOn")
			: value === "denied"
				? t("privacy.cookieChoiceOff")
				: t("privacy.cookieChoiceUndecided");

	return (
		<div className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-start">
			<IconChartBar size={18} stroke={1.75} className="mt-0.5 shrink-0 text-muted-foreground" />
			<div className="min-w-0 flex-1">
				<div className="text-sm font-medium text-foreground">{t("privacy.cookieChoices")}</div>
				<div className="mt-0.5 text-xs text-muted-foreground">{t("privacy.cookieChoicesHelp")}</div>
				<dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
					<dt className="text-muted-foreground">{t("consent:categories.analytics.label")}</dt>
					<dd className="text-foreground">{valueLabel(state.analytics)}</dd>
					{/* В приложении рекламы нет и вопроса о ней тоже (OXEM-549) — решение
					    о персонализации меняется в IEC ID по ссылке ниже. */}
					{IS_CAPACITOR ? null : (
						<>
							<dt className="text-muted-foreground">{t("consent:categories.advertising.label")}</dt>
							<dd className="text-foreground">{valueLabel(state.advertising)}</dd>
						</>
					)}
				</dl>
				<button
					type="button"
					onClick={() => openIecIdSection("legal")}
					className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary cursor-pointer pointer-coarse:min-h-11"
				>
					{t("privacy.analyticsManageInIecId")}
					<IconExternalLink size={12} stroke={1.75} />
				</button>
			</div>
			<Button
				variant="outline"
				className="shrink-0 pointer-coarse:h-11 sm:mt-0.5"
				onClick={onChange}
			>
				{t("privacy.cookieChoicesChange")}
			</Button>
		</div>
	);
}
